import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Divider, LinearProgress, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import WidgetCard from '../../components/home/WidgetCard';
import WidgetSkeleton from '../../components/home/WidgetSkeleton';
import { useWidgetSummary } from '../../hooks/useWidgetSummary';
import { formatMetric, isEmptySummary, buildLinkTo } from '../../widgets/summaryUtils';
import { getNumberComma, formatCompactKorean } from '../../utils/numberUtils';

// 메인화면 역할별 구성 1단계(2026-10-23) - COUNT_LIST/BAR_LIST/TREND_LINE 세 가지 렌더 유형을
// 전부 이 컨테이너 하나가 처리한다(위젯마다 컴포넌트를 새로 만들지 않는다 - 조사 문서 "bito 구현"
// 절의 "위젯을 역할마다 새로 만들 필요가 없다" 원칙을 프론트에도 그대로 적용).
// 403을 받으면 레이아웃-권한이 어긋난 신호이므로 에러 카드를 보여주지 않고 카드를 통째로 숨긴다.
//
// 비주얼 리뉴얼(2026-10-23): 카드 틀은 DetailTemplate(검은 제목바) 대신 WidgetCard(흰 배경 +
// 옅은 테두리)를 쓴다. "전체 보기 →"는 카드 우상단(헤더 action)으로 옮겼고, 하단 footer에는
// "HH:mm 기준"만 남긴다. 데이터 fetch는 useWidgetSummary로 뽑아 KpiStrip과 공유한다.

const RowsList = ({ rows }) => (
  <List dense disablePadding>
    {rows.slice(0, 5).map((r, i) => (
      <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
        <ListItemText
          primary={r.label}
          secondary={[r.sub, r.dt].filter(Boolean).join(' · ') || undefined}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
        {r.value && (
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', ml: 1, whiteSpace: 'nowrap' }}>
            {r.value}
          </Typography>
        )}
      </ListItem>
    ))}
  </List>
);

const BarsList = ({ bars }) => (
  <Stack spacing={1.25} sx={{ mt: 1 }}>
    {bars.map((b, i) => {
      const pct = b.target ? Math.min(100, Math.round((b.value / b.target) * 100)) : null;
      return (
        <Box key={i}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">{b.label}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {getNumberComma(b.value || 0)}{b.target ? ` / ${getNumberComma(b.target)}` : ''}
            </Typography>
          </Box>
          {pct !== null && <LinearProgress variant="determinate" value={pct} sx={{ height: 5, borderRadius: 1, mt: 0.5 }} />}
        </Box>
      );
    })}
  </Stack>
);

// "작고 얇게" - sparkline에 가깝게 높이를 줄이고 축은 최소한만 보인다(2026-10-23 비주얼 리뉴얼).
const TrendChart = ({ bars }) => (
  <Box sx={{ width: '100%', height: 120, mt: 1 }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={bars} margin={{ top: 5, right: 6, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis width={40} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={formatCompactKorean} />
        <Tooltip formatter={(v) => `${getNumberComma(v)}원`} />
        <Line type="monotone" dataKey="value" name="발행금액" stroke="#1976d2" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </Box>
);

const AgingChart = ({ bars }) => (
  <Box sx={{ width: '100%', height: 110, mt: 1 }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={bars} margin={{ top: 5, right: 6, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
        <YAxis width={40} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={formatCompactKorean} />
        <Tooltip formatter={(v) => `${getNumberComma(v)}원`} />
        <Bar dataKey="value" name="금액" fill="#ef6c00" radius={[3, 3, 0, 0]} maxBarSize={28} />
      </BarChart>
    </ResponsiveContainer>
  </Box>
);

const GenericWidgetContainer = ({ widgetCd, title, renderTyp }) => {
  const { loading, forbidden, summary, error } = useWidgetSummary(widgetCd);

  if (forbidden) {
    return null; // 권한-레이아웃 불일치 - 에러 카드 대신 카드를 숨긴다.
  }

  const cardTitle = summary?.title || title;
  const cardSubtitle = summary?.subtitle;
  const viewAllAction = !loading && !error && summary?.link?.path && (
    <Button size="small" component={RouterLink} to={buildLinkTo(summary.link)}>
      전체 보기 →
    </Button>
  );
  const footer = !loading && !error && summary?.asOf && (
    <Typography variant="caption" color="text.disabled">
      {summary.asOf} 기준
    </Typography>
  );

  return (
    <WidgetCard title={cardTitle} subtitle={cardSubtitle} action={viewAllAction} footer={footer}>
      {loading && <WidgetSkeleton />}

      {!loading && error && (
        <Typography variant="body2" color="text.secondary">
          불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
        </Typography>
      )}

      {!loading && !error && summary && (
        <Box>
          {isEmptySummary(summary) ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              해당하는 데이터가 없습니다.
            </Typography>
          ) : (
            <>
              {summary.metric && (
                <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {formatMetric(summary.metric)}
                </Typography>
              )}
              {summary.metric?.label && (
                <Typography variant="caption" color="text.secondary">{summary.metric.label}</Typography>
              )}

              {renderTyp === 'TREND_LINE' && summary.bars?.length > 0 && (
                <TrendChart bars={summary.bars} />
              )}
              {renderTyp === 'BAR_LIST' && summary.bars?.length > 0 && (
                widgetCd === 'MANAGE_AR_AGING'
                  ? <AgingChart bars={summary.bars} />
                  : <BarsList bars={summary.bars} />
              )}

              {summary.rows?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <RowsList rows={summary.rows} />
                </>
              )}
            </>
          )}
        </Box>
      )}
    </WidgetCard>
  );
};

export default GenericWidgetContainer;
